import { AnimatePresence, motion } from "framer-motion";
import { Fragment, useState } from "react";
import { X, ArrowUpRight } from "lucide-react";
import { menuData } from "@/data/menu";
import logo from "@/assets/Final Logo1.png";
import logoDark from "@/assets/Final Logo2.png";
import logoLight from "@/assets/small-size.png";
import { Link, useLocation, useNavigate } from "react-router-dom";



function MenuLinkLabel({ label }: { label: string }) {
  const words = label.trim().split(/\s+/);
  const lastWord = words.pop();
  const leadingText = words.length > 0 ? `${words.join(" ")} ` : "";

  return (
    <span>
      {leadingText}
      <span className="whitespace-nowrap">
        {lastWord}
        <ArrowUpRight className="inline opacity-0 group-hover:opacity-100 ml-1 w-3.5 h-3.5 text-brand transition shrink-0" />
      </span>
    </span>
  );
}

function normalizePath(path: string) {
  return path.replace(/\/$/, "") || "/";
}

function getMenuByKey(key: string) {
  return menuData.find((menu) => menu.key === key);
}

function getParentKeyByPath(path: string) {
  const normalizedPath = normalizePath(path);
  
  for (const menu of menuData) {
    if (menu.columns?.some((col) => col.items.some((item) => normalizePath(item.path) === normalizedPath))) {
      return menu.key;
    }
  }

  return "";
}

export function BurgerMenu({ open, onClose }: { open: boolean; onClose: () => void }) {
  const location = useLocation();
  const [activeKey, setActiveKey] = useState("");
  const [hoverKey, setHoverKey] = useState("");
  const activeMenuKey = getParentKeyByPath(location.pathname);
  const activeKeyToShow = hoverKey || activeKey || activeMenuKey || (menuData.length > 0 ? menuData[0].key : "");
  const active = menuData.find((m) => m.key === activeKeyToShow) ?? menuData[0];
  const navigate = useNavigate();

  const isDirectNavItem = (key: string) =>
    key === "contact-us" || key === "blogs";
  const handleDirectNav = (key: string) => {
    if (key === "contact-us") {
      navigate("/contact");
      onClose();
    }
    if (key === "blogs") {
      navigate("/blogs");
      onClose();
    }
    if (key === "case-studies") {
      navigate("/case-studies");
      onClose();
    }
  };

  return (
    <AnimatePresence>
      {/* MOBILE + TABLET */}

      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.25 }}
          className="z-[60] fixed inset-0 flex justify-center items-center bg-black/30 backdrop-blur-sm p-3 md:p-8"
          onClick={onClose}
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.96, y: 12 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.97, y: 8 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            onClick={(e) => e.stopPropagation()}
            className="relative bg-background shadow-[0_40px_120px_-30px_rgba(0,0,0,0.35)] border border-border/60 rounded-[28px] w-full max-w-[1280px] h-[90vh] max-h-[920px] overflow-hidden"
            style={{
              backgroundImage:
                "radial-gradient(600px 300px at 90% 110%, color-mix(in oklab, var(--color-brand) 18%, transparent), transparent 70%), radial-gradient(500px 260px at 10% -10%, color-mix(in oklab, var(--color-brand) 10%, transparent), transparent 70%)",
            }}
          >
            {/* MOBILE + TABLET */}
            <div className="lg:hidden block px-5 py-6 h-full overflow-x-hidden overflow-y-auto">
              <div className="mb-8">
                <Link to="/" onClick={onClose}>
                  {/* <img
                    src={logoDark}
                    alt="Santhila"
                    className="hidden dark:block w-auto h-9 md:h-[48px]"
                  /> */}
                  <img
                    src={logo}
                    alt="Santhila logo — Enterprise AI solutions, Industrial IoT platforms, and product engineering"
                    className="dark:hidden block drop-shadow-[0_0_12px_rgba(255,255,255,0.40)] w-40 h-auto object-contain object-left"
                  />

                  {/* Dark mode logo */}
                  <img
                    src={logoDark}
                    alt="Santhila logo — Enterprise AI solutions, Industrial IoT platforms, and product engineering"
                    className="hidden dark:block w-40 h-auto object-contain object-left"
                  />
                  {/* <img
                    src={logoLight}
                    alt="Santhila"
                    className="dark:hidden block w-auto h-9 md:h-[48px]"
                  /> */}
                </Link>
              </div>

              <button
                aria-label="Close menu"
                onClick={onClose}
                className="top-4 right-4 absolute place-items-center grid bg-background/80 border border-border/60 rounded-full w-10 h-10"
              >
                <X className="w-4 h-4" />
              </button>

              <div className="space-y-1" onMouseLeave={() => setHoverKey("")}>
                {menuData.map((m) => {
                  const isRouteActive = activeMenuKey === m.key;
                  const isActive = activeKey === m.key;
                  const menuItem = getMenuByKey(m.key) ?? m;

                  return (
                    <Fragment key={m.key}>
                      <div className="pb-2 border-border/60 border-b">
                        {/* MENU TITLE */}
                        <button
                          onMouseEnter={() => {
                            if (!isDirectNavItem(m.key)) setHoverKey(m.key);
                          }}
                          onClick={() => {
                            if (isDirectNavItem(m.key)) {
                              handleDirectNav(m.key);
                            } else if (m.key === "case-studies" && isActive) {
                              handleDirectNav("case-studies");
                            } else {
                              setActiveKey(isActive ? "" : m.key);
                            }
                          }}
                          className="flex justify-between items-center py-2 w-full text-left"
                        >
                          <span className={`font-semibold text-sm uppercase tracking-[0.14em] transition ${isDirectNavItem(m.key)
                            ? isRouteActive || isActive
                              ? "text-brand"
                              : "text-foreground/70 hover:text-brand"
                            : isRouteActive || isActive
                              ? "text-brand"
                              : "text-foreground hover:text-brand"
                            }`}>
                            {m.label}
                          </span>

                          {!isDirectNavItem(m.key) ? (
                            <ArrowUpRight
                              className={`h-4 w-4 transition-transform duration-300 ${isActive ? "rotate-45 text-brand" : "text-heading"
                                }`}
                            />
                          ) : null}
                        </button>

                        {/* DROPDOWN */}
                        <AnimatePresence>
                          {isActive && (
                            <motion.div
                              initial={{ height: 0, opacity: 0 }}
                              animate={{ height: "auto", opacity: 1 }}
                              exit={{ height: 0, opacity: 0 }}
                              transition={{ duration: 0.3 }}
                              className="overflow-hidden"
                            >
                              <div className="space-y-2 pt-1 pb-3">
                                {m.key === "case-studies" && (
                                  <Link
                                    to="/case-studies"
                                    onClick={onClose}
                                    className="group block py-1 font-semibold text-brand text-sm transition"
                                  >
                                    <MenuLinkLabel label="All Case Studies" />
                                  </Link>
                                )}
                                {menuItem.columns
                                  .flatMap((col) => col.items)
                                  .map((it, i) => {
                                    const isSubActive = normalizePath(location.pathname) === normalizePath(it.path);
                                    return (
                                      <Link
                                        key={i}
                                        to={it.path}
                                        onClick={() => {
                                          setActiveKey(m.key);
                                          onClose();
                                        }}
                                        className={`group block py-1 text-sm transition ${isSubActive
                                          ? "text-brand font-semibold"
                                          : "text-muted-foreground hover:text-brand"
                                          }`}
                                      >
                                        <MenuLinkLabel label={it.label} />
                                      </Link>
                                    );
                                  })}
                              </div>
                            </motion.div>
                          )}
                        </AnimatePresence>
                      </div>
                    </Fragment>
                  );
                })}
              </div>
            </div>

            <div
              className="hidden lg:grid grid-cols-[340px_1fr] h-full"
              onMouseLeave={() => setHoverKey("")}
            >
              {/* LEFT PANEL */}
              <div className="relative bg-background/70 px-6 md:px-8 py-7 md:py-9 border-border/60 md:border-r border-b md:border-b-0 overflow-hidden">
                <Link to="/" onClick={onClose} className="inline-flex items-center mb-8 md:mb-10">
                  {/* <img
                    src={logoDark}
                    alt="Santhila"
                    className="hidden dark:block w-auto h-9 md:h-12"
                  /> */}
                  <img
                    src={logo}
                    alt="Santhila logo — Enterprise AI solutions, Industrial IoT platforms, and product engineering"
                    className="dark:hidden block drop-shadow-[0_0_12px_rgba(255,255,255,0.40)] w-50 h-9 md:h-[80px]"
                  />

                  {/* Dark mode logo */}
                  <img
                    src={logoDark}
                    alt="Santhila logo — Enterprise AI solutions, Industrial IoT platforms, and product engineering"
                    className="hidden dark:block w-50 h-9 md:h-[80px]"
                  />
                  {/* <img
                    src={logoLight}
                    alt="Santhila"
                    className="dark:hidden block w-auto h-9 md:h-12"
                  /> */}
                </Link>
                <ul className="space-y-1">
                  {menuData.map((m) => {
                    const isActive = m.key === activeKeyToShow;
                    const isRouteActive = activeMenuKey === m.key;
                    return (
                      <Fragment key={m.key}>
                        <li>
                          <button
                            onMouseEnter={() => {
                              if (!isDirectNavItem(m.key)) {
                                setHoverKey(m.key);
                                setActiveKey(m.key);
                              }
                            }}
                            onClick={() => {
                              if (isDirectNavItem(m.key) || m.key === "case-studies") {
                                handleDirectNav(m.key);
                              } else {
                                setActiveKey(m.key);
                                setHoverKey(m.key);
                              }
                            }}
                            className={`group relative flex w-full items-center justify-start gap-2 rounded-md py-2 pl-4 pr-3 text-left text-[15px] font-medium transition-colors ${isDirectNavItem(m.key)
                              ? isRouteActive || isActive
                                ? "text-brand"
                                : "text-foreground/70 hover:text-brand"
                              : isRouteActive || isActive
                                ? "text-brand"
                                : "text-foreground hover:text-brand"
                              }`}
                          >
                            {/* <span
                            className={`absolute left-0 top-1/2 h-7 w-[3px] -translate-y-1/2 rounded-full bg-brand transition-opacity ${isActive ? "opacity-100" : "opacity-0"
                              }`}
                          /> */}
                            <span>{m.label}</span>
                            <ArrowUpRight
                              className="h-4 w-4 text-brand opacity-0 transition-all duration-300 group-hover:opacity-100 group-hover:-translate-y-0.5 group-hover:translate-x-0.5"
                            />
                          </button>
                        </li>
                      </Fragment>
                    );
                  })}
                </ul>
              </div>

              {/* RIGHT PANEL */}
              <div className="relative px-6 md:px-12 py-8 md:py-12 h-full overflow-hidden">
                <button
                  aria-label="Close menu"
                  onClick={onClose}
                  className="top-4 md:top-6 right-4 md:right-6 absolute place-items-center grid bg-background/80 hover:bg-muted border border-border/60 rounded-full w-10 h-10 transition"
                >
                  <X className="w-4 h-4" />
                </button>

                <AnimatePresence mode="wait">
                  <motion.div
                    key={active.key}
                    initial={{ opacity: 0, y: 12 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -6 }}
                    transition={{ duration: 0.3 }}
                  >
                    {active.key === "case-studies" ? (
                      <Link
                        to="/case-studies"
                        onClick={onClose}
                        className="inline-flex items-center gap-2 mb-3 font-semibold text-[2rem] text-brand uppercase tracking-[0.06em] hover:underline"
                      >
                        {active.label}
                        <ArrowUpRight className="w-6 h-6 text-brand" />
                      </Link>
                    ) : (
                      <p className="mb-3 font-semibold text-[2rem] text-brand uppercase tracking-[0.06em]">
                        {active.label}
                      </p>
                    )}
                    {/* <h3 className="mb-10 max-w-2xl font-display text-heading md:text-[40px] text-3xl leading-tight">
                      {`Engineered solutions for ${active.label.toLowerCase()}.`}
                    </h3> */}

                    <div
                      className={`grid gap-x-6 gap-y-2 ${active.key === "who-we-are" ? "md:grid-cols-1" : "md:grid-cols-3"}`}
                    >
                      {active.columns
                        .flatMap((col) => col.items)
                        .map((it, i) => {
                          const isSubActive = normalizePath(location.pathname) === normalizePath(it.path);
                          return (
                            <motion.div
                              key={`${it.path}-${i}`}
                              initial={{ opacity: 0, y: 0 }}
                              animate={{ opacity: 1, y: 0 }}
                              transition={{ delay: 0.04 + i * 0.025 }}
                            >
                              <Link
                                to={it.path}
                                onClick={onClose}
                                className={`group flex justify-between items-center py-2 text-[15px] transition ${isSubActive
                                  ? "text-brand font-semibold"
                                  : "text-foreground/85 hover:text-brand"
                                  }`}
                              >
                                <MenuLinkLabel label={it.label} />
                              </Link>
                            </motion.div>
                          );
                        })}
                    </div>
                  </motion.div>
                </AnimatePresence>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
